/**
 * useMicDrop — le micro tombé (`micDrop`), branché :
 *  - la règle relue à chaque changement du live, et une fois encore au terme du délai si le micro reste coupé ;
 *  - tombé, une trace par épisode (`mic_dropped`, un événement Bugsnag : sans elle, la panne ne se voyait
 *    nulle part), puis la salle le rouvre d'elle-même : tout de suite, et à chaque micro branché
 *    (`devicechange`). L'accès est acquis (le micro libre, `micGate`) : rouvrir ne pose aucune question ;
 *  - tant qu'il ne se rouvre pas, l'écran le dit, avec le geste qui le demande (`toggleMic`) ;
 *  - au-delà de `MIC_DROP_MAX_EPISODES` épisodes dans ce live (un micro qui retombe aussitôt rouvert), la salle
 *    ne le rouvre plus d'elle-même et ne le trace plus : seul le bandeau reste, avec son geste.
 *
 * Au web seulement, comme le micro muet : dans l'app, c'est le téléphone qui tient le micro, et rien de ce
 * chemin n'y a été vu.
 */
import { type LocalParticipant } from "livekit-client"
import { useCallback, useEffect, useReducer, useRef } from "react"

import { describeNonError } from "../../shared/nonError"
import { IS_WEB } from "../../shared/platform"
import { type EngineTelemetry } from "../../shared/telemetry"
import { initialMicDrop, MIC_DROP_MS, micDropReducer } from "./micDrop"
import { MIC_CAPTURE } from "./useMicRefusal"

/** Les micros tombés qu'un live trace et rouvre de lui-même : au-delà, le bandeau seul. */
export const MIC_DROP_MAX_EPISODES = 3

export type UseMicDropOptions = {
    localParticipant: Pick<LocalParticipant, "setMicrophoneEnabled">
    dialogue: boolean
    /**
     * La salle connectée, le micro libre (`micGate.held` faux) et aucun refus déjà dit (`micRefusal`) : la
     * salle l'ouvre d'elle-même, sans question du navigateur.
     */
    ready: boolean
    micOn: boolean
    telemetry: EngineTelemetry
}

export type MicDropController = {
    dropped: boolean
    /**
     * L'élève coupe son micro (`request`, la coupure) : ce n'est pas un micro tombé. Compté dès le geste, pour
     * qu'une coupure lente ne passe pas pour une chute ; défait si la coupure échoue.
     */
    cut: (request: Promise<unknown>) => void
}

export function useMicDrop(options: UseMicDropOptions): MicDropController {
    const { localParticipant, dialogue, ready, micOn, telemetry } = options
    const [state, send] = useReducer(micDropReducer, initialMicDrop)
    // Le délai court (la règle a posé `offSince`) : un seul relevé, à son terme, et rien une fois tombé.
    const offSince = IS_WEB && !state.dropped ? state.offSince : null

    useEffect(() => {
        if (!IS_WEB) return undefined
        const observe = () => send({ type: "observed", at: Date.now(), dialogue, ready, micOn })
        observe()
        if (offSince == null) return undefined
        const timer = setTimeout(observe, Math.max(0, offSince + MIC_DROP_MS - Date.now()))
        return () => clearTimeout(timer)
    }, [dialogue, ready, micOn, offSince])

    const reopen = useCallback(
        (event: string) => {
            localParticipant.setMicrophoneEnabled(true, MIC_CAPTURE).catch((cause: unknown) => {
                const name = cause instanceof Error ? cause.name : describeNonError(cause).kind
                telemetry.transition("mic_dropped", "mic_dropped", `reopen_failed_${event}`, { cause: name })
            })
        },
        [localParticipant, telemetry]
    )

    const droppedRef = useRef(false)
    const episodesRef = useRef(0)
    useEffect(() => {
        if (state.dropped === droppedRef.current) return
        droppedRef.current = state.dropped
        if (!state.dropped) {
            telemetry.transition("mic_dropped", "mic_ok", "cleared")
            return
        }
        episodesRef.current += 1
        if (episodesRef.current > MIC_DROP_MAX_EPISODES) return
        // `wasOn` sépare le micro perdu en séance du micro qui ne s'est jamais ouvert dans ce Dialogue.
        telemetry.error("mic_dropped", null, { wasOn: state.wasOn, episode: episodesRef.current })
        reopen("dropped")
    }, [state.dropped, state.wasOn, telemetry, reopen])

    // Un micro rebranché (le casque revenu) : la salle le rouvre sans attendre l'élève.
    useEffect(() => {
        const devices = typeof navigator === "undefined" ? undefined : navigator.mediaDevices
        if (!state.dropped || episodesRef.current > MIC_DROP_MAX_EPISODES || devices?.addEventListener == null) {
            return undefined
        }
        const onChange = () => reopen("devicechange")
        devices.addEventListener("devicechange", onChange)
        return () => devices.removeEventListener("devicechange", onChange)
    }, [state.dropped, reopen])

    const cut = useCallback((request: Promise<unknown>) => {
        send({ type: "cut" })
        request.catch(() => send({ type: "cutFailed" }))
    }, [])
    return { dropped: state.dropped, cut }
}
